import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  subscriptionStatusLabel,
  isSubscriptionActive,
  needsPaymentAttention,
} from '@pirouette/shared';
import { useAllSubscriptions } from '@/data/stripe';
import { supabase } from '@/lib/supabase';
import { Badge } from '@/components/ui/Badge';
import { Empty } from '@/components/ui/Empty';

export const Route = createFileRoute('/admin/billing/subscriptions')({
  component: AdminSubs,
});

function useFamilyNames(ids: string[]) {
  return useQuery({
    enabled: ids.length > 0,
    queryKey: ['family-names', ids.sort().join('|')],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('families')
        .select('id, primary_guardian_name')
        .in('id', ids);
      if (error) throw error;
      const map = new Map<string, string>();
      for (const f of (data ?? []) as {
        id: string;
        primary_guardian_name: string;
      }[]) {
        map.set(f.id, f.primary_guardian_name);
      }
      return map;
    },
  });
}

function AdminSubs() {
  const subs = useAllSubscriptions();
  const familyIds = useMemo(() => {
    const set = new Set<string>();
    for (const s of subs.data ?? []) set.add(s.family_id);
    return [...set];
  }, [subs.data]);
  const families = useFamilyNames(familyIds);

  const active = (subs.data ?? []).filter((s) => isSubscriptionActive(s.status));
  const attention = (subs.data ?? []).filter((s) => needsPaymentAttention(s.status));
  const inactive = (subs.data ?? []).filter(
    (s) => !isSubscriptionActive(s.status) && !needsPaymentAttention(s.status),
  );

  return (
    <div className="space-y-8">
      <header className="flex items-baseline justify-between gap-3">
        <div>
          <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
            Billing · Admin
          </p>
          <h1 className="mt-2">Subscriptions</h1>
        </div>
        <nav className="flex gap-3 text-[var(--text-sm)]">
          <Link to="/admin/billing/plans" className="underline">
            Manage plans
          </Link>
        </nav>
      </header>

      {attention.length > 0 && (
        <section aria-labelledby="attention-heading">
          <h2 id="attention-heading" className="text-[var(--text-xl)]">
            Needs attention
          </h2>
          <SubList subs={attention} familyNames={families.data ?? new Map()} tone="warn" />
        </section>
      )}

      <section aria-labelledby="active-heading">
        <h2 id="active-heading" className="text-[var(--text-xl)]">
          Active ({active.length})
        </h2>
        {active.length === 0 ? (
          <Empty title="No active subscriptions" description="" className="mt-4" />
        ) : (
          <SubList subs={active} familyNames={families.data ?? new Map()} tone="success" />
        )}
      </section>

      {inactive.length > 0 && (
        <section aria-labelledby="inactive-heading">
          <h2 id="inactive-heading" className="text-[var(--text-xl)]">
            Inactive ({inactive.length})
          </h2>
          <SubList subs={inactive} familyNames={families.data ?? new Map()} tone="neutral" />
        </section>
      )}
    </div>
  );
}

interface SubRow {
  id: string;
  family_id: string;
  status: ReturnType<typeof subscriptionStatusLabel> extends string
    ? Parameters<typeof subscriptionStatusLabel>[0]
    : never;
  stripe_subscription_id: string;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
}

function SubList({
  subs,
  familyNames,
  tone,
}: {
  subs: SubRow[];
  familyNames: Map<string, string>;
  tone: 'success' | 'warn' | 'neutral';
}) {
  return (
    <ul className="mt-4 grid gap-2">
      {subs.map((s) => (
        <li key={s.id} className="surface flex items-center justify-between p-4">
          <div>
            <p className="font-medium">
              <Link
                to="/admin/billing/$familyId"
                params={{ familyId: s.family_id }}
                className="underline"
              >
                {familyNames.get(s.family_id) ?? '(family)'}
              </Link>
            </p>
            <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
              {s.stripe_subscription_id}
              {s.cancel_at_period_end && ' · cancels at period end'}
              {s.current_period_end && (
                <>
                  {' · '}
                  next: {new Date(s.current_period_end).toLocaleDateString()}
                </>
              )}
            </p>
          </div>
          <Badge tone={tone}>{subscriptionStatusLabel(s.status)}</Badge>
        </li>
      ))}
    </ul>
  );
}
